import { autoinject, bindable, processContent } from 'aurelia-framework';
import { TabClass, TabSelector, TabsBase, getTabPanelForTab, isTabActive } from 'app/shared/elements/tabs-base';
import { Key, getKey } from 'app/shared/lib/keys';
import { debounce } from 'app/shared/lib/decorators';

export interface ITabs {
    activateTab(elementOrId: Element | string): void;
}

export interface ITabChangedEventDetails {
    readonly tabs: ITabs;
    readonly activeIndex: number;
    readonly activeTab: Element;
}

export type TabChangedEvent = CustomEvent<ITabChangedEventDetails>;

@autoinject()
@processContent(EssTabsHorizontalCustomElement.processContent)
export class EssTabsHorizontalCustomElement extends TabsBase {

    ////****************** Fields and Properties ************************************////
    @bindable
    public label: string = '';

    ////****************** Element References ***************************************////

    ////****************** Lifecycle events (in order of execution) *****************////
    protected attached() {
        this.tabObserver.observe(this.$tablist, { childList: true });
        this.resizeObserver.observe(this.element, {}); // recompute overflow when tab bar is resized
        this.recomputeTabs();
    }

    protected detached() {
        this.tabObserver.disconnect();
        this.resizeObserver.disconnect();
    }

    ////****************** Events ***************************************************////
    protected handleKeydown(event: KeyboardEvent): true | void {
        if(!this.focusedTab) {
            return;
        }

        // INFO [Matt] reorder tabs into their visual order
        const tabs = this.tabs.reduce(([tabs], tab) => {
            const set = tabs;
            set.push(tab);
            return [tabs]
        }, [[] as HTMLElement[], [] as HTMLElement[]]).flat();

        // increment the focused tab index
        let focusedTabIndex = tabs.indexOf(this.focusedTab!);
        switch(getKey(event)) {
            case Key.Left:
                focusedTabIndex = (tabs.length + focusedTabIndex - 1) % tabs.length;
                break;
            case Key.Right:
                focusedTabIndex = (focusedTabIndex + 1) % tabs.length;
                break;
            case Key.Enter:
            case Key.Space:
                return this.focusedTab?.click();
            default:
                return true;
        }

        // update the focused tab
        this.updateFocus(tabs[focusedTabIndex]);
        this.focusedTab.focus();
    }

    ////****************** Functions ************************************************////
    @debounce()
    protected recomputeTabs(): void {
        this.$tablist.classList.add(TabClass.Processing);
        this.tabs = Array.from(this.element.querySelectorAll<HTMLElement>(TabSelector.Tab));
        if(this.activeTab === null || !this.tabs.includes(this.activeTab)) {
            const defaultTab: HTMLElement | undefined = this.tabs.find((tab) => getTabPanelForTab(tab) !== null);
            const activeTab: HTMLElement | undefined = this.tabs.find((tab) => isTabActive(tab)) || defaultTab;
            if(activeTab !== undefined) {
                this.activateTab(activeTab);
            }
        }
        this.$tablist.classList.remove(TabClass.Processing);
    }

    protected consolidateTabs(): void { }
}
